import React, { useMemo } from 'react'
import styled, { keyframes } from 'styled-components'

const VB_W = 220
const VB_H = 84
const GROUND_Y = 74
const LOOP_SECONDS = 5
const STAGGER = 0.15
const GECKO_YELLOW = '#FEC600'
const ORB_FILL = 'rgba(255, 255, 255, 0.92)'

const ORB_XS = [15, 41, 67, 93, 119, 145, 171, 197]
const CHART_Y_MIN = 14
const CHART_Y_MAX = 50

const orbMove = keyframes`
  0%   { fill: ${ORB_FILL}; transform: translate(0, 0); }
  12%  { fill: ${ORB_FILL}; transform: translate(0, 0); }
  26%  { fill: ${ORB_FILL}; transform: translate(0, var(--chart-y)); }
  60%  { fill: ${ORB_FILL}; transform: translate(0, var(--chart-y)); }
  62%  { fill: ${GECKO_YELLOW}; transform: translate(0, var(--chart-y)) scale(1.6); }
  64%  { fill: ${ORB_FILL}; transform: translate(0, var(--chart-y)); }
  78%  { fill: ${ORB_FILL}; transform: translate(0, 0); }
  100% { fill: ${ORB_FILL}; transform: translate(0, 0); }
`

const lineDraw = keyframes`
  0%   { stroke-dashoffset: var(--len); opacity: 0; }
  28%  { stroke-dashoffset: var(--len); opacity: 0; }
  32%  { stroke-dashoffset: var(--len); opacity: 0.8; }
  40%  { stroke-dashoffset: 0; opacity: 0.8; }
  60%  { stroke-dashoffset: 0; opacity: 0.8; }
  64%  { stroke-dashoffset: calc(var(--len) * -1); opacity: 0.8; }
  68%  { opacity: 0; }
  100% { stroke-dashoffset: calc(var(--len) * -1); opacity: 0; }
`

const scanSweep = keyframes`
  0%   { transform: translateX(-10px); opacity: 0; }
  58%  { transform: translateX(-10px); opacity: 0; }
  60%  { transform: translateX(0); opacity: 1; }
  84%  { transform: translateX(${VB_W - 10}px); opacity: 1; }
  86%  { transform: translateX(${VB_W + 10}px); opacity: 0; }
  100% { transform: translateX(${VB_W + 10}px); opacity: 0; }
`

const groundPulse = keyframes`
  0%, 100% { opacity: 0.25; }
  50%      { opacity: 0.55; }
`

const Svg = styled.svg`
  width: 12rem;
  height: 4.25rem;
  overflow: visible;

  @media (prefers-reduced-motion: reduce) {
    circle,
    line {
      animation: none !important;
    }
  }
`

const Ground = styled.line`
  stroke: rgba(255, 255, 255, 0.3);
  stroke-width: 1;
  stroke-dasharray: 3 5;
  animation: ${groundPulse} 2s ease-in-out infinite;
`

const Orb = styled.circle<{ $delay: number }>`
  fill: ${ORB_FILL};
  transform-box: fill-box;
  transform-origin: center;
  animation: ${orbMove} ${LOOP_SECONDS}s cubic-bezier(0.22, 1, 0.36, 1) infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`

const Connector = styled.line<{ $delay: number; $len: number }>`
  stroke: rgba(255, 255, 255, 0.7);
  stroke-width: 1.4;
  stroke-linecap: round;
  fill: none;
  --len: ${({ $len }) => $len};
  stroke-dasharray: ${({ $len }) => $len};
  stroke-dashoffset: ${({ $len }) => $len};
  animation: ${lineDraw} ${LOOP_SECONDS}s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`

const ScanLine = styled.line`
  stroke: ${GECKO_YELLOW};
  stroke-width: 1.5;
  stroke-linecap: round;
  filter: drop-shadow(0 0 4px ${GECKO_YELLOW});
  animation: ${scanSweep} ${LOOP_SECONDS}s linear infinite;
`

export const TimeSeriesLoadingAnimation: React.FC = () => {
  const orbs = useMemo(
    () =>
      ORB_XS.map((x) => ({
        x,
        y: CHART_Y_MIN + Math.random() * (CHART_Y_MAX - CHART_Y_MIN),
      })),
    []
  )

  return (
    <Svg viewBox={`0 0 ${VB_W} ${VB_H}`} aria-hidden='true'>
      <Ground x1={0} y1={GROUND_Y + 0.5} x2={VB_W} y2={GROUND_Y + 0.5} />

      {orbs.slice(0, -1).map((a, i) => {
        const b = orbs[i + 1]
        const len = Math.hypot(b.x - a.x, b.y - a.y)
        return (
          <Connector
            key={`line-${i}`}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            $len={len}
            $delay={i * STAGGER}
          />
        )
      })}

      {orbs.map((o, i) => (
        <Orb
          key={`orb-${i}`}
          cx={o.x}
          cy={GROUND_Y}
          r={3.5}
          style={{ '--chart-y': `${o.y - GROUND_Y}px` } as React.CSSProperties}
          $delay={i * STAGGER}
        />
      ))}

      <ScanLine x1={0} y1={4} x2={0} y2={VB_H - 4} />
    </Svg>
  )
}
